import { useState, useEffect } from 'react';
import { Box, Typography, Button, Tabs, Tab, CircularProgress } from '@mui/material';
import { Plus, Database } from 'lucide-react';
import { useCurrentUser } from '@lingaai/user-management';
import { ConnectionCard } from '../components/data/ConnectionCard';
import { SyncLogTable } from '../components/data/SyncLogTable';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';

const DATA_PIPELINE_URL = import.meta.env.VITE_DATAPIPE_URL || 'http://localhost:3002';

interface Connection {
  id: string;
  name: string;
  source_system: string;
  status: string;
  sync_mode: string;
  sync_frequency: string;
  last_sync_at: string | null;
  next_sync_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export function Data() {
  const { data: user } = useCurrentUser();
  const [tabValue, setTabValue] = useState(0);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConnections = async () => {
    if (!user?.tenant_id) return;

    try {
      const response = await fetch(`${DATA_PIPELINE_URL}/api/connections?tenant_id=${user.tenant_id}`);
      if (response.ok) {
        const data = await response.json();
        setConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Failed to fetch connections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchConnections();
    }
  }, [user]);

  const handleAddIntegration = async () => {
    if (!user) return;

    try {
      // Get session token from data-pipeline
      const response = await fetch(`${DATA_PIPELINE_URL}/api/nango-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          tenant_id: user.tenant_id,
          email: user.email,
          full_name: user.full_name,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create session');
      }

      // Dynamically import Nango to avoid SSR issues
      const { default: Nango } = await import('@nangohq/frontend');
      const nango = new Nango({});

      // Open Nango Connect UI (shows loading indicator)
      const connect = nango.openConnectUI({
        onEvent: (event: any) => {
          if (event.type === 'connect') {
            console.log('Connection created:', event);
            // Refresh connections list
            fetchConnections();
          } else if (event.type === 'close') {
            console.log('Connect UI closed');
          }
        },
      });

      // Get session token and set it (triggers UI to load)
      const { sessionToken } = await response.json();
      connect.setSessionToken(sessionToken);
    } catch (error) {
      console.error('Failed to open Nango Connect UI:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#6366f1' }}>
            <Database size={ICON_SIZES.xl} strokeWidth={ICON_STROKE} />
          </Box>
          <Typography variant="h4">Data Integrations</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Plus size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />} onClick={handleAddIntegration}>
          Add Integration
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Connections (${connections.length})`} />
        <Tab label="Sync History" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : connections.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No integrations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Connect your data sources to start syncing
            </Typography>
            <Button variant="contained" startIcon={<Plus size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />} onClick={handleAddIntegration}>
              Add Your First Integration
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: 2,
            }}
          >
            {connections.map((connection) => (
              <ConnectionCard key={connection.id} connection={connection} onUpdate={fetchConnections} />
            ))}
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <SyncLogTable />
      </TabPanel>
    </Box>
  );
}
